import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertCircle } from 'lucide-react';

interface DataUpdatedModalProps {
  isOpen: boolean;
  pageName?: string;
  onReload: () => void;
  onDismiss?: () => void;
}

export const DataUpdatedModal: React.FC<DataUpdatedModalProps> = ({
  isOpen,
  pageName,
  onReload,
  onDismiss,
}) => {
  if (!isOpen) return null;

  // Format page name nicely
  const formatPageName = (name?: string): string => {
    if (!name) return 'Data';
    
    // Convert common page names to readable format
    const pageNameMap: Record<string, string> = {
      'loans': 'Loans',
      'loan-form': 'Loan Form',
      'loan-detail': 'Loan Detail',
      'admin': 'Admin',
      'profile': 'Profile',
      'dashboard': 'Dashboard',
      'pricing-comparison': 'Pricing Comparison',
      'valuation-reports': 'Valuation Reports',
      'market-color': 'Market Color',
      'benchmark-rates': 'Benchmark Rates',
      'fx-rates': 'FX Rates',
      'rpx-adjustments': 'RPX Adjustments',
      'loan-analytics': 'Loan Analytics',
    };
    
    return pageNameMap[name.toLowerCase()] || name;
  };

  const displayPageName = formatPageName(pageName);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" data-test-id="data-updated-modal-overlay">
      <Card className="w-full max-w-md mx-4 shadow-xl" data-test-id="data-updated-modal">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-full">
              <AlertCircle className="h-5 w-5 text-blue-600" />
            </div>
            <CardTitle className="text-blue-600" data-test-id="data-updated-modal-title">
              Data Updated
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <p className="text-gray-700 mb-2" data-test-id="data-updated-modal-message">
              <span className="font-semibold text-gray-900">{displayPageName}</span> page data has been updated by another user.
            </p>
            <p className="text-sm text-gray-600">
              Please reload the page to see the latest changes.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={onReload}
              className="flex-1 flex items-center gap-2"
              data-test-id="data-updated-modal-reload-button"
            >
              <RefreshCw className="h-4 w-4" />
              Reload Page
            </Button>
            {onDismiss && (
              <Button
                type="button"
                variant="outline"
                onClick={onDismiss}
                className="flex-1"
                data-test-id="data-updated-modal-dismiss-button"
              >
                Dismiss
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

